import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { useTranslation } from 'react-i18next';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';

import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort } from '@syncfusion/ej2-react-grids';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import type { ResourceRequirementDto } from '../../api/models/ResourceRequirementDto';
import type { OperationDto } from '../../api/models/OperationDto';
import type { ResourceTypeDto } from '../../api/models/ResourceTypeDto';
import type { ResourceDto } from '../../api/models/ResourceDto';
import type { ResourceRequirementResourceDto } from '../../api/models/ResourceRequirementResourceDto';
import type { CrudFormProps } from '../../components/CrudPage';
import { useErrorHandler } from '../../utils/errorHandler';
import { generateTempUUID } from '../../utils/uuid';
import { sortByOperationNumber } from '../../utils/operationOrder';
import { ResourceTypeControllerService } from '../../api/services/ResourceTypeControllerService';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';

// ---------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------

const resourceRequirementSchema = z.object({
  fromOperationId: z.string().min(1, 'validation.required'),
  toOperationId: z.string().min(1, 'validation.required'),
  fromRelation: z.enum(['START', 'END']),
  toRelation: z.enum(['START', 'END']),
  resourceTypeUuid: z.string().min(1, 'validation.required'),
  resources: z.array(z.any()).optional(), // overwritten in onSubmit
  conditional: z.boolean(),
  conditionalOperationId: z.string().optional(),
  conditionalResourceIds: z.array(z.string()).optional()
});

type ResourceRequirementFormData = z.infer<typeof resourceRequirementSchema>;

// ---------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------

interface ResourceRequirementDetailDialogProps
  extends CrudFormProps<ResourceRequirementDto & { id: string }> {
  availableOperations: OperationDto[];
}

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

const ResourceRequirementDetailDialog: React.FC<ResourceRequirementDetailDialogProps> = ({
  value,
  onClose,
  onSave,
  availableOperations
}) => {
  const { t } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const [visible, setVisible] = useState(false);

  // Master data
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeDto[]>([]);
  const [resources, setResources] = useState<ResourceDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Grid-Status: Allowed Resources (Hauptgrid)
  const [gridRows, setGridRows] = useState<
    { resource: ResourceDto; selected: boolean; quantity: number }[]
  >([]);

  // Ref mirror of gridRows: Syncfusion's footerTemplate caches the OK click handler
  // when the dialog first becomes visible. At that point gridRows is still [], because
  // resourceTypeUuid through useWatch only takes effect in the next render. Without the ref, onSubmit
  // reads a stale closure with gridRows=[] and saves an empty resource list.
  const gridRowsRef = useRef<typeof gridRows>([]);
  gridRowsRef.current = gridRows;

  const resourcesGridRef = useRef<GridComponent | null>(null);
  const conditionalGridRef = useRef<GridComponent | null>(null);

  // -------------------------------------------------------------------
  // React Hook Form
  // -------------------------------------------------------------------

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<ResourceRequirementFormData>({
    resolver: zodResolver(resourceRequirementSchema),
    mode: 'onSubmit',
    defaultValues: {
      fromOperationId: '',
      toOperationId: '',
      fromRelation: 'START',
      toRelation: 'END',
      resourceTypeUuid: '',
      resources: [],
      conditional: false,
      conditionalOperationId: '',
      conditionalResourceIds: []
    }
  });

  const resourceTypeUuid = useWatch({ control, name: 'resourceTypeUuid', defaultValue: '' });
  const conditionalEnabled = useWatch({ control, name: 'conditional', defaultValue: false });
  const conditionalOperationId = useWatch({
    control,
    name: 'conditionalOperationId',
    defaultValue: ''
  });
  const conditionalResourceIds = useWatch({
    control,
    name: 'conditionalResourceIds',
    defaultValue: []
  });

  const currentValue = watch();

  // -------------------------------------------------------------------
  // Load data
  // -------------------------------------------------------------------

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rt, rs] = await Promise.all([
          ResourceTypeControllerService.findAll(),
          ResourceControllerService.findAll()
        ]);
        setResourceTypes(rt || []);
        setResources(rs || []);
      } catch (err) {
        console.error('Error loading resources', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // -------------------------------------------------------------------
  // Fill the form initially (edit case) and open the dialog
  // -------------------------------------------------------------------

  useEffect(() => {
    if (loading) return;

    // When a value is present, fill the form with it
    if (value) {
      reset({
        fromOperationId: value.fromOperationId || '',
        toOperationId: value.toOperationId || '',
        fromRelation: value.fromRelation || 'START',
        toRelation: value.toRelation || 'END',
        resourceTypeUuid: value.resourceTypeUuid || '',
        resources: value.resources || [],
        conditional: value.conditional ?? false,
        conditionalOperationId: value.conditionalOperationId || '',
        conditionalResourceIds: value.conditionalResourceIds || []
      });
    } else {
      // New entry - reset the form
      reset({
        fromOperationId: '',
        toOperationId: '',
        fromRelation: 'START',
        toRelation: 'END',
        resourceTypeUuid: '',
        resources: [],
        conditional: false,
        conditionalOperationId: '',
        conditionalResourceIds: []
      });
    }
    
    // Always open the dialog once loading is false
    setVisible(true);
  }, [value, loading, reset]);

  // -------------------------------------------------------------------
  // Initialize the allowed resources grid / on a resource type change
  // -------------------------------------------------------------------

  useEffect(() => {
    if (!resourceTypeUuid || loading || resources.length === 0) {
      setGridRows([]);
      return;
    }

    const typeResources = resources.filter(
      r => String(r.resourceTypeUuid) === String(resourceTypeUuid)
    );

    const existingResourcesFromValue = (value?.resources || []) as ResourceRequirementResourceDto[];
    const existingMap = new Map<string, ResourceRequirementResourceDto>();
    existingResourcesFromValue.forEach(rr => {
      if (rr.resourceId) {
        existingMap.set(String(rr.resourceId), rr);
      }
    });

    const rows = typeResources.map(resource => {
      const existing = resource.uuid ? existingMap.get(String(resource.uuid)) : undefined;
      return {
        resource,
        selected: existing ? true : false,
        quantity: existing?.quantity ?? 1
      };
    });

    setGridRows(rows);
  }, [resourceTypeUuid, resources, loading, value?.resources]);

  // -------------------------------------------------------------------
  // Operation-Optionen
  // -------------------------------------------------------------------

  // Same order as in the operations tab of the routing (Vorgänge)
  const operationOptions = useMemo(
    () =>
      sortByOperationNumber(availableOperations).map(op => ({
        text: op.name || 'Unnamed Operation',
        value: op.uuid || ''
      })),
    [availableOperations]
  );

  // -------------------------------------------------------------------
  // Conditional section
  // -------------------------------------------------------------------

  const conditionalOperation = useMemo(
    () => availableOperations.find(op => op.uuid === conditionalOperationId),
    [availableOperations, conditionalOperationId]
  );

  const conditionalAllowedResources = useMemo(() => {
    if (!conditionalOperation || !conditionalOperation.operationModes) return [];
    const map = new Map<string, { id: string; name: string }>();
    conditionalOperation.operationModes.forEach(mode => {
      if (mode.resourceId && mode.resourceName) {
        map.set(mode.resourceId, { id: mode.resourceId, name: mode.resourceName });
      }
    });
    return Array.from(map.values());
  }, [conditionalOperation]);

  const conditionalGridData = useMemo(
    () =>
      conditionalAllowedResources.map(res => ({
        resourceId: res.id,
        resourceName: res.name,
        selected: (conditionalResourceIds || []).includes(res.id)
      })),
    [conditionalAllowedResources, conditionalResourceIds]
  );


  // -------------------------------------------------------------------
  // Handler: Allowed Resources (Hauptgrid)
  // -------------------------------------------------------------------

  const handleResourceSelectionChange = (resourceUuid: string, selected: boolean) => {
    setGridRows(prev =>
      prev.map(row =>
        row.resource.uuid === resourceUuid ? { ...row, selected } : row
      )
    );
  };

  const handleResourceQuantityChange = (resourceUuid: string, quantity: number) => {
    const value = quantity || 0;
    setGridRows(prev =>
      prev.map(row =>
        row.resource.uuid === resourceUuid ? { ...row, quantity: value } : row
      )
    );
  };

  // -------------------------------------------------------------------
  // Handler: Conditional
  // -------------------------------------------------------------------

  const handleConditionalToggle = (checked: boolean) => {
    setValue('conditional', checked, { shouldDirty: true });
    if (!checked) {
      setValue('conditionalOperationId', '', { shouldDirty: true });
      setValue('conditionalResourceIds', [], { shouldDirty: true });
    }
  };

  const handleConditionalOperationChange = (operationId: string) => {
    setValue('conditionalOperationId', operationId || '', { shouldDirty: true });
    setValue('conditionalResourceIds', [], { shouldDirty: true });
  };

  const handleConditionalResourceSelectionChange = (resourceId: string, selected: boolean) => {
    const current = conditionalResourceIds || [];
    let next: string[];
    if (selected) {
      next = current.includes(resourceId) ? current : [...current, resourceId];
    } else {
      next = current.filter(id => id !== resourceId);
    }
    setValue('conditionalResourceIds', next, { shouldDirty: true });
  };

  // -------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------

  const onSubmit = useCallback(async (data: ResourceRequirementFormData) => {
    try {
      if (!data.fromOperationId || !data.toOperationId) {
        throw new Error('From and To Operation are required');
      }

      const currentGridRows = gridRowsRef.current;
      const resourcesToSave: ResourceRequirementResourceDto[] = currentGridRows
        .filter(r => r.selected && r.resource.uuid)
        .map(r => ({
          uuid: generateTempUUID(),
          resourceRequirementId: value?.uuid,
          resourceId: r.resource.uuid!,
          quantity: r.quantity ?? 1
        }));

      const requirement: ResourceRequirementDto = {
        uuid: value?.uuid,
        fromOperationId: data.fromOperationId,
        toOperationId: data.toOperationId,
        fromRelation: data.fromRelation,
        toRelation: data.toRelation,
        resourceTypeUuid: data.resourceTypeUuid,
        resources: resourcesToSave,
        conditional: data.conditional,
        conditionalOperationId: data.conditional ? data.conditionalOperationId || undefined : undefined,
        conditionalResourceIds:
          data.conditional && (data.conditionalResourceIds || []).length > 0
            ? data.conditionalResourceIds
            : undefined
      };

      const { id, ...requirementData } = { ...requirement, id: value?.id };
      await onSave(requirementData as any);
      // Only close dialog if save was successful
      setVisible(false);
    } catch (err) {
      handleError(err, 'saving resource requirement');
      // Don't close the dialog on error
    }
  }, [value, onSave, handleError]);

  const handleClose = useCallback(() => {
    setVisible(false);
    // Delay onClose to allow React to clean up Grid components
    setTimeout(() => {
      onClose();
    }, 100);
  }, [onClose]);

  // -------------------------------------------------------------------
  // Footer Template
  // -------------------------------------------------------------------

  const footerTemplate = useCallback(
    () => (
      <div className="flex justify-end space-x-2 p-2">
        <ButtonComponent type="button" onClick={handleClose} cssClass="e-outline">
          {t('actions.cancel')}
        </ButtonComponent>
        <ButtonComponent
          type="button"
          disabled={isSubmitting}
          cssClass="e-primary"
          onClick={async () => {
            await handleSubmit(onSubmit)();
          }}
        >
          {t('actions.ok')}
        </ButtonComponent>
      </div>
    ),
    [handleClose, handleSubmit, isSubmitting, onSubmit, t]
  );

  // -------------------------------------------------------------------
  // Loading guard - the dialog is always rendered, but only visible once ready
  // -------------------------------------------------------------------

  // Don't return null - always render the dialog, but control visibility

  // -------------------------------------------------------------------
  // Dialog Body (useCallback like OperationDetailDialog and OperationRelationshipDetailDialog)
  // -------------------------------------------------------------------

  const dialogBody = useCallback(() => (
    <div className="p-4 space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* From Relation and From Operation - First Row */}
        <div className="grid grid-cols-[150px_1fr] gap-4">
          {/* From Relation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('routing.form.fromRelation')} *
            </label>
            <DropDownListComponent
              dataSource={[
                { text: t('form.relationType.START'), value: 'START' },
                { text: t('form.relationType.END'), value: 'END' }
              ]}
              fields={{ text: 'text', value: 'value' }}
              value={currentValue.fromRelation || 'START'}
              change={args => setValue('fromRelation', args.value)}
            />
          </div>

          {/* From Operation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('routing.form.fromOperation')} *
            </label>
            <DropDownListComponent
              dataSource={operationOptions}
              fields={{ text: 'text', value: 'value' }}
              placeholder={t('form.selectOperation')}
              value={currentValue.fromOperationId || ''}
              change={args => setValue('fromOperationId', args.value, { shouldDirty: true })}
              cssClass={errors.fromOperationId ? 'e-error' : ''}
            />
          </div>
        </div>

        {/* To Relation and To Operation - Second Row */}
        <div className="grid grid-cols-[150px_1fr] gap-4">
          {/* To Relation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('routing.form.toRelation')} *
            </label>
            <DropDownListComponent
              dataSource={[
                { text: t('form.relationType.START'), value: 'START' },
                { text: t('form.relationType.END'), value: 'END' }
              ]}
              fields={{ text: 'text', value: 'value' }}
              value={currentValue.toRelation || 'END'}
              change={args => setValue('toRelation', args.value)}
            />
          </div>

          {/* To Operation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('routing.form.toOperation')} *
            </label>
            <DropDownListComponent
              dataSource={operationOptions}
              fields={{ text: 'text', value: 'value' }}
              placeholder={t('form.selectOperation')}
              value={currentValue.toOperationId || ''}
              change={args => setValue('toOperationId', args.value, { shouldDirty: true })}
              cssClass={errors.toOperationId ? 'e-error' : ''}
            />
          </div>
        </div>

        {/* Resource Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('routing.form.resourceType')} *
          </label>
          <DropDownListComponent
            dataSource={resourceTypes.filter(rt => rt.name && rt.uuid)}
            fields={{ text: 'name', value: 'uuid' }}
            placeholder={t('routing.form.selectResourceType')}
            value={currentValue.resourceTypeUuid || ''}
            change={args =>
              setValue('resourceTypeUuid', args.value, { shouldValidate: true, shouldDirty: true })
            }
            cssClass={errors.resourceTypeUuid ? 'e-error' : ''}
            allowFiltering={true}
            filterBarPlaceholder={t('actions.search')}
          />
        </div>

        {/* Allowed Resources Grid */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('routing.form.allowedResources', 'Erlaubte Ressourcen')} *
          </label>
          <div className="h-60">
            <GridComponent
              key={`resources-grid-${resourceTypeUuid}`}
              ref={resourcesGridRef}
              dataSource={gridRows}
              height="100%"
              allowSorting={true}
              allowFiltering={false}
              enableHeaderFocus={true}
              locale="de-DE"
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="selected"
                  headerText={t('routing.form.flag')}
                  width={80}
                  template={(props: any) => {
                    const item = props.data || props;
                    const resourceUuid = item.resource?.uuid;
                    if (!resourceUuid) return null;
                    return (
                      <div className="flex items-center justify-center h-full">
                        <CheckBoxComponent
                          checked={item.selected ?? false}
                          change={(e: any) => {
                            handleResourceSelectionChange(resourceUuid, e.checked ?? false);
                          }}
                        />
                      </div>
                    );
                  }}
                />
                <ColumnDirective
                  field="resource.name"
                  headerText={t('form.resource')}
                  width="auto"
                  template={(props: any) => {
                    const item = props.data || props;
                    return <span>{item.resource?.name || 'NO NAME'}</span>;
                  }}
                />
                <ColumnDirective
                  field="quantity"
                  headerText={t('routing.form.quantity')}
                  width={150}
                  template={(props: any) => {
                    const item = props.data || props;
                    const resourceUuid = item.resource?.uuid;
                    const disabled = !item.selected;
                    if (!resourceUuid) return null;
                    const quantity = item.quantity ?? 1;
                    return (
                      <input
                        type="number"
                        value={quantity}
                        min="0.01"
                        step="0.01"
                        disabled={disabled}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                        style={{ 
                          backgroundColor: disabled ? '#f3f4f6' : 'white',
                          cursor: disabled ? 'not-allowed' : 'text'
                        }}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value) || 0;
                          if (newValue >= 0.01) {
                            handleResourceQuantityChange(resourceUuid, newValue);
                          }
                        }}
                        onBlur={(e) => {
                          const newValue = parseFloat(e.target.value) || 1;
                          if (newValue < 0.01) {
                            handleResourceQuantityChange(resourceUuid, 1);
                          }
                        }}
                      />
                    );
                  }}
                />
              </ColumnsDirective>
              <Inject services={[Sort]} />
            </GridComponent>
          </div>
        </div>

        {/* Conditional Checkbox */}
        <div className="mt-4">
          <div className="flex items-center">
            <CheckBoxComponent
              checked={conditionalEnabled}
              change={(e: any) => handleConditionalToggle(!!e.checked)}
            />
            <label
              className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
              onClick={() => handleConditionalToggle(!conditionalEnabled)}
            >
              {t('routing.form.conditional', 'ist konditional')}
            </label>
          </div>
        </div>

        {/* Conditional Section */}
        {conditionalEnabled && (
          <>
            {/* Conditional Operation */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('routing.form.conditionalOperation', 'wenn Vorgang')} *
              </label>
              <DropDownListComponent
                dataSource={operationOptions}
                fields={{ text: 'text', value: 'value' }}
                placeholder={t('form.selectOperation')}
                value={conditionalOperationId || ''}
                change={args => handleConditionalOperationChange(args.value || '')}
              />
            </div>

            {/* Conditional Resources Grid */}
            {conditionalOperationId && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t(
                    'routing.form.conditionalResources',
                    'Primär-Ressource (ODER-verknüpft) nutzt'
                  )}{' '}
                  *
                </label>
                {conditionalGridData.length > 0 ? (
                  <div className="h-60">
                    <GridComponent
                      key={`conditional-grid-${conditionalOperationId}-${conditionalGridData.length}`}
                      ref={conditionalGridRef}
                      dataSource={conditionalGridData.map(r => ({ ...r }))}
                      height="100%"
                      allowSorting={true}
                      allowFiltering={false}
                      enableHeaderFocus={true}
                      locale="de-DE"
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="selected"
                          headerText={t('routing.form.flag')}
                          width={80}
                          template={(props: any) => {
                            const item = props;
                            return (
                              <div key={`conditional-checkbox-${item.resourceId}`} className="flex items-center justify-center h-full">
                                <CheckBoxComponent
                                  key={`conditional-cb-${item.resourceId}`}
                                  checked={item.selected ?? false}
                                  change={(e: any) =>
                                    handleConditionalResourceSelectionChange(
                                      item.resourceId,
                                      e.checked ?? false
                                    )
                                  }
                                />
                              </div>
                            );
                          }}
                        />
                        <ColumnDirective
                          field="resourceName"
                          headerText={t('form.resource')}
                          width="auto"
                          template={(props: any) => {
                            const item = props;
                            return <span>{item.resourceName || ''}</span>;
                          }}
                        />
                      </ColumnsDirective>
                      <Inject services={[Sort]} />
                    </GridComponent>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4">
                    <div className="text-gray-500 text-sm italic">
                      {conditionalOperation
                        ? t(
                            'routing.form.noResourcesAvailable',
                            'Keine Ressourcen verfügbar'
                          )
                        : t(
                            'routing.form.selectOperationFirst',
                            'Bitte wählen Sie zuerst eine Operation aus'
                          )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </form>
    </div>
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSubmit not used directly in body; conditionalOperation added
  ), [currentValue, errors, operationOptions, resourceTypes, resourceTypeUuid, gridRows, conditionalEnabled, conditionalOperationId, conditionalGridData, conditionalOperation, handleResourceSelectionChange, handleResourceQuantityChange, handleConditionalToggle, handleConditionalOperationChange, handleConditionalResourceSelectionChange, setValue, handleSubmit, t]);

  // -------------------------------------------------------------------
  // Dialog
  // -------------------------------------------------------------------

  return (
    <DialogComponent
      visible={visible && !loading}
      isModal={true}
      header={value?.uuid ? t('actions.edit') : t('actions.add')}
      showCloseIcon={true}
      closeOnEscape={true}
      width="900px"
      height="950px"
      close={handleClose}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="resource-requirement-detail-dialog"
      zIndex={10000}
      footerTemplate={footerTemplate}
    >
      {loading ? (
        <div className="p-4 text-center">
          <div className="text-gray-500">{t('actions.loading', 'Laden...')}</div>
        </div>
      ) : (
        dialogBody()
      )}
    </DialogComponent>
  );
};

export default ResourceRequirementDetailDialog;
